import * as Sentry from "@sentry/react";

type Level = "info" | "warning" | "error";

/**
 * Flood-proof triage logging for the @nudge-ai/audio-store local audio backup
 * and the GCS recording pipeline.
 *
 * A recording emits a chunk every ~10s for up to 2-3h. If any tier (local store,
 * GCS upload, the handler itself) is wedged, the matching error path runs on
 * EVERY chunk. So the single rule in this module:
 *
 *   Every Sentry EVENT (captureException / captureMessage) is bounded by a HARD
 *   per-session cap that NEVER resets.
 *
 * A resetting time-window is NOT enough — "3 per minute" is still ~500 events
 * over a long session. With absolute caps, each failure kind contributes at most
 * a few events for the WHOLE session and then degrades to a breadcrumb.
 * Breadcrumbs are local-only (Sentry keeps ~100, oldest dropped, and ships them
 * only attached to an actual captured event), so they can never flood. Net
 * result: flooding is impossible by construction, not by tuning.
 *
 * Fully defensive — logging must never throw into the critical recording path.
 */

// Hard per-session capture cap per failure kind. Never reset for the life of the
// page/recording session.
const CAPTURE_CAP = 3;
const captureCounts = new Map<string, number>();

/**
 * Emit a Sentry event for `key` at most `cap` times per session; every call past
 * the cap — and any call that would otherwise throw while capturing — degrades to
 * a breadcrumb. Never throws into the caller.
 */
function boundedCapture(
  key: string,
  cap: number,
  capture: () => void,
  breadcrumb: { level: Level; message: string; data?: Record<string, unknown> },
): void {
  try {
    const count = captureCounts.get(key) ?? 0;
    if (count < cap) {
      captureCounts.set(key, count + 1);
      capture();
      return;
    }
    Sentry.addBreadcrumb({ category: "audio-store", ...breadcrumb });
  } catch {
    // logging must never throw into a storage / recording operation
  }
}

function toError(error: unknown): Error {
  return error instanceof Error ? error : new Error(String(error));
}

/** Test-only: reset all per-session capture caps between cases. */
export function __resetAudioStoreLogCaps(): void {
  captureCounts.clear();
}

/**
 * Log an audio-store connection-state change (open/closed/closing/blocked/
 * recovered/error). Breadcrumb-only — these never become standalone Sentry
 * events, so they cannot flood; the throttle here only de-noises the console.
 */
const lastConnectionAt = new Map<string, number>();
const CONNECTION_THROTTLE_MS = 30_000;
export function logAudioStoreConnection(event: {
  state: string;
  engine?: string;
  reason?: string;
  error?: Error;
}): void {
  try {
    const key = `${event.state}:${event.engine ?? ""}`;
    const now = Date.now();
    if (now - (lastConnectionAt.get(key) ?? 0) < CONNECTION_THROTTLE_MS) return;
    lastConnectionAt.set(key, now);

    const level = event.state === "error" ? "error" : event.state === "blocked" ? "warning" : "info";
    Sentry.addBreadcrumb({
      category: "audio-store",
      level,
      message: `connection ${event.state}${event.engine ? ` (${event.engine})` : ""}`,
      data: { reason: event.reason, error: event.error?.message },
    });
    if (level !== "info") {
      console.warn(
        `[audio-store] connection ${event.state}${event.engine ? ` (${event.engine})` : ""}`,
        event.reason ?? "",
      );
    }
  } catch {
    // logging must never throw into a storage operation
  }
}

/**
 * Local-backup write failure (the store rejected on every tier). Captured at most
 * CAPTURE_CAP times per session, then breadcrumb-only.
 */
export function logAudioStoreSaveFailure(error: unknown, data: Record<string, unknown>): void {
  boundedCapture(
    "save-failure",
    CAPTURE_CAP,
    () =>
      Sentry.captureException(toError(error), {
        extra: { ...data, context: "audio-store local save failed on all tiers" },
      }),
    { level: "warning", message: "local save still failing (capture cap reached this session)", data },
  );
}

/**
 * GCS chunk upload failure. The recording continues on the local backup, so this
 * is a degraded-but-recoverable signal — captured at most CAPTURE_CAP times per
 * session (a down GCS would otherwise fail every ~10s chunk), then breadcrumb.
 */
export function logGcsUploadFailure(error: unknown, data: Record<string, unknown>): void {
  boundedCapture(
    "gcs-upload-failure",
    CAPTURE_CAP,
    () =>
      Sentry.captureException(toError(error), {
        extra: { ...data, context: "GCS chunk upload failed; recording continues on local backup" },
      }),
    { level: "warning", message: "GCS upload still failing (capture cap reached this session)", data },
  );
}

/**
 * Any other recording-pipeline error (unhandled dataavailable handler error,
 * MediaRecorder error event, …). Keyed by `context` so distinct failure kinds
 * each get their own cap, but every kind is still bounded per session.
 */
export function logAudioPipelineError(error: unknown, data: Record<string, unknown> & { context: string }): void {
  boundedCapture(
    `pipeline:${data.context}`,
    CAPTURE_CAP,
    () => Sentry.captureException(toError(error), { extra: data }),
    { level: "error", message: `audio pipeline error: ${data.context} (capture cap reached this session)`, data },
  );
}

/**
 * Signal that a StorageQuotaError recovery sweep ran — i.e. we reclaimed local
 * storage by clearing the backup of backend-confirmed done-processing
 * recordings. Every sweep adds a (non-flooding) breadcrumb, but we emit a
 * captured event ONLY when bytes were actually reclaimed (cleared.length > 0),
 * and even then at most CAPTURE_CAP times per session. A wedged 2-3h session lets
 * a sweep run every minute (the cooldown) for hundreds of ticks; capturing each
 * would flood Sentry (NUDGE-FRONTEND-3AV) with no new signal.
 */
export function logStorageQuotaRecovery(result: {
  attempted: number;
  cleared: string[];
  skipped: number;
  reclaimedBytes: number | null;
  ranSweep: boolean;
}): void {
  const summary = {
    cleared_count: result.cleared.length,
    attempted: result.attempted,
    skipped: result.skipped,
    reclaimed_bytes: result.reclaimedBytes,
    ran_sweep: result.ranSweep,
  };
  try {
    Sentry.addBreadcrumb({
      category: "audio-store",
      level: "info",
      message: `storage quota recovery: cleared ${result.cleared.length}/${result.attempted} done-processing recordings`,
      data: summary,
    });
  } catch {
    // logging must never throw into a storage operation
  }
  if (result.cleared.length > 0) {
    boundedCapture(
      "quota-recovery",
      CAPTURE_CAP,
      () =>
        Sentry.captureMessage("audio-store storage quota recovery", {
          level: "info",
          extra: { ...summary, cleared: result.cleared.slice(0, 20) },
        }),
      { level: "info", message: "storage quota recovery (capture cap reached this session)", data: summary },
    );
  }
}

/**
 * The local backup is full and a recovery sweep found NOTHING safe to clear (all
 * remaining local audio is unconfirmed / not-yet-durable). This recurs on every
 * chunk (~10s) for the rest of a wedged session, so we capture it to Sentry
 * exactly ONCE per session (cap 1); every recurrence after that is a breadcrumb.
 */
export function logStorageQuotaExhausted(data: Record<string, unknown>): void {
  boundedCapture(
    "quota-exhausted",
    1,
    () =>
      Sentry.captureMessage("audio-store storage quota exhausted; nothing safe to reclaim", {
        level: "warning",
        extra: { ...data, context: "local backup full and no done-processing recordings to clear" },
      }),
    { level: "warning", message: "storage quota still exhausted (suppressing repeat captures this session)", data },
  );
}
